import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ITEM_WARRANTIES } from 'src/common/constants';
import { selectItemFormData } from 'src/store/selectors/itemForm';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import { setItemFormData, clearItemFormData } from 'src/store/actions/itemForm';
import Button from '../Button';
import Col from '../Col';
import { Heading } from '../Form';
import { GenericDialogue } from '../Dialogue/Dialogue';
import Input from '../Input';
import Row from '../Row';
import Select from '../Select';
import MultiSelect from 'react-select';
import Loader from '../Loader';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { selectValidation } from 'src/store/selectors/Validation';
import { clearValidation } from 'src/store/actions/validation';
import { fetchProperties } from 'src/store/actions/properties';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { selectProperties } from 'src/store/selectors/properties';
import Checkbox from '../Checkbox';
import { selectPRLoader } from 'src/store/selectors/PropertyLoader';
import { useFetchServicesProvidedTypes } from 'src/SharedCustomHooks/UseContactFormOptions/useContactFormOptions';

interface ItemFormProps {
  onClose: () => void;
  onSaveItem: (data: any) => void;
}

const ItemForm = ({ onClose, onSaveItem }: ItemFormProps) => {
  const dispatch = useDispatch();

  const itemFormData = useSelector(selectItemFormData) as any;
  const loader = useSelector(selectFormLoader) as any;
  const validation = useSelector(selectValidation) as any;
  const user = useSelector(selectAuthenticatedUser) as any;
  const properties = useSelector(selectProperties) as any;
  const prloader = useSelector(selectPRLoader) as any;

  const { allItemTypes } = useFetchServicesProvidedTypes();

  const onSetItemFormData = useCallback(
    (data: any) => dispatch(setItemFormData(data)),
    [dispatch]
  );
  const onFetchProperties = useCallback(
    (...args: any[]) => dispatch(fetchProperties(...args) as any),
    [dispatch]
  );

  const [buttonPressed, setButtonPressed] = useState('');

  useEffect(() => {
    onFetchProperties(
      user.id,
      user.roles[0].role.name,
      true,
      itemFormData && itemFormData.id ? 'edit' : 'add'
    );
    if (itemFormData && itemFormData.id) {
      onSetItemFormData({
        isSelectAll: itemFormData.properties.length === properties.length,
      });
    }
  }, []);

  useEffect(() => {
    if (validation.status === true) {
      if (validation.message === 'Item' && buttonPressed === 'SaveAndClose') {
        onClose();
        dispatch(clearItemFormData());
        dispatch(clearValidation());
      } else if (
        validation.message === 'Item' &&
        buttonPressed === 'SaveAndAdd'
      ) {
        dispatch(clearItemFormData());
        dispatch(clearValidation());
      }
    }
  }, [validation, buttonPressed, onClose, dispatch]);

  const onChange = (e: any) => {
    let type = allItemTypes.filter(
      (type: any) => type.value === e.target.value
    );

    onSetItemFormData({
      typeId: e.target.value,
      typeName: type[0].label,
    });
  };

  const saveItem = () => {
    onSaveItem(itemFormData);
  };

  const onSelectAll = () => {
    if (itemFormData.isSelectAll && itemFormData.isSelectAll === true) {
      onSetItemFormData({
        selectedProperties: [],
        isSelectAll: false,
      });
    } else {
      onSetItemFormData({
        selectedProperties:
          properties && properties.length > 0
            ? properties.map((pty: any) => ({ value: pty.id, label: pty.name }))
            : [],
        isSelectAll: true,
      });
    }
  };

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      title={(itemFormData.id ? 'Edit' : 'Add an') + ' Item'}
      footer={
        <>
          {loader || prloader ? <Loader center={true} /> : null}
          {!itemFormData.id ? (
            <Button
              color="semantic"
              size="small"
              label="Save and Add Another"
              disabled={
                !itemFormData.name ||
                !itemFormData.typeId ||
                !itemFormData.warrantyYears ||
                !itemFormData.selectedProperties.length
              }
              onClick={() => {
                setButtonPressed('SaveAndAdd');
                saveItem();
              }}
            />
          ) : (
            <div />
          )}
          <Button
            color="teal"
            size="small"
            label="Save Item"
            disabled={
              !itemFormData.name ||
              !itemFormData.typeId ||
              !itemFormData.warrantyYears ||
              !itemFormData.selectedProperties.length
            }
            onClick={() => {
              setButtonPressed('SaveAndClose');
              saveItem();
            }}
          />
        </>
      }
    >
        <>
          <Row mdCenter>
            <Col xs="12" md="6">
              <Heading>Item Details</Heading>
              <Input
                value={itemFormData.name}
                onChange={(e: any) =>
                  onSetItemFormData({ name: e.target.value })
                }
                label="Name/Description"
                placeholder="eg., Microwave"
                required
                block
              />

              <label className="file__label">
                Property
                <div className="select__required" />
              </label>
              <Checkbox
                value={
                  itemFormData.isSelectAll && itemFormData.isSelectAll === true
                }
                label="All Properties"
                onChange={onSelectAll}
              />
              <MultiSelect
                value={
                  itemFormData.selectedProperties
                    ? itemFormData.selectedProperties
                    : []
                }
                closeMenuOnSelect={false}
                isMulti
                onChange={(values: any) =>
                  onSetItemFormData({
                    selectedProperties: values,
                    isSelectAll: values.length === properties.length,
                  })
                }
                options={
                  properties && properties.length > 0
                    ? properties.map((pty: any) => ({
                        value: pty.id,
                        label: pty.name,
                      }))
                    : []
                }
              />
              <Select
                value={itemFormData.typeId ? itemFormData.typeId : ''}
                placeholder="(Select Type)"
                onChange={(e: any) => onChange(e)}
                options={allItemTypes}
                label="Type"
              />
              <Select
                value={
                  itemFormData.warrantyYears ? itemFormData.warrantyYears : ''
                }
                placeholder="(Select Warranty)"
                onChange={(e: any) =>
                  onSetItemFormData({ warrantyYears: e.target.value })
                }
                options={ITEM_WARRANTIES.map((itemWarranty: any) => ({
                  value: itemWarranty,
                  label: `${itemWarranty === 0 ? 'NA' : itemWarranty} ${itemWarranty === 0 ? '' : 'year'}${itemWarranty > 1 ? 's' : ''}`,
                }))}
                label="Warranty"
              />
              <Input
                value={itemFormData.makeName}
                onChange={(e: any) =>
                  onSetItemFormData({ makeName: e.target.value })
                }
                label="Make"
                placeholder="eg., Samsung"
                block
              />
              <Input
                value={itemFormData.model}
                onChange={(e: any) =>
                  onSetItemFormData({ model: e.target.value })
                }
                label="Model"
                placeholder="eg., BMO700BSS"
                block
              />
            </Col>
          </Row>
        </>
    </GenericDialogue>
  );
};

export default ItemForm;
